import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  CopyOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { UploadProps } from "antd";
import { apiService } from "../../services/api";
import type { App, AppDomainFile } from "../../types/api";

const { Text, Link } = Typography;

export interface DomainFilesTabProps {
  editingApp: App | null;
  isEditMode: boolean;
}

const formatBytes = (bytes?: number | null): string => {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Нормалізує введений шлях так само, як бекенд, для прев'ю URL. */
const normalizePath = (raw: string): string => {
  let p = (raw || "").trim().split("?")[0].split("#")[0];
  if (!p) return "";
  p = "/" + p.replace(/^\/+/, "");
  p = p.replace(/\/{2,}/g, "/");
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return p;
};

export const DomainFilesTab: React.FC<DomainFilesTabProps> = ({
  editingApp,
  isEditMode,
}) => {
  const appId = editingApp?.id;
  const domain = editingApp?.domain;

  const [files, setFiles] = useState<AppDomainFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [path, setPath] = useState("");

  const publicUrl = useCallback(
    (filePath: string) =>
      domain ? `https://${domain}${filePath}` : filePath,
    [domain]
  );

  const loadFiles = useCallback(async () => {
    if (!appId) return;
    try {
      setLoading(true);
      const data = await apiService.getDomainFiles(appId);
      setFiles(data);
    } catch (error) {
      message.error("Помилка завантаження списку файлів");
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    if (isEditMode && appId) loadFiles();
  }, [isEditMode, appId, loadFiles]);

  const handleUpload: UploadProps["customRequest"] = async (options) => {
    const { file, onSuccess, onError } = options;
    const normalized = normalizePath(path);
    if (!appId) return;
    if (!normalized || normalized === "/") {
      message.warning("Спочатку вкажіть шлях віддачі, напр. /assets/app.js");
      onError?.(new Error("no path"));
      return;
    }
    try {
      setUploading(true);
      await apiService.uploadDomainFile(appId, file as File, normalized);
      message.success(`Файл залито на ${normalized}`);
      onSuccess?.({}, new XMLHttpRequest());
      await loadFiles();
    } catch (error: any) {
      const detail =
        error?.response?.data?.message || "Помилка завантаження файлу";
      message.error(Array.isArray(detail) ? detail.join(", ") : detail);
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (record: AppDomainFile) => {
    if (!appId) return;
    try {
      const blob = await apiService.getDomainFileContent(appId, record.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = record.filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      message.error(`Помилка завантаження файлу ${record.filename}`);
    }
  };

  const handleDelete = async (record: AppDomainFile) => {
    if (!appId) return;
    try {
      await apiService.deleteDomainFile(appId, record.id);
      message.success("Файл видалено");
      await loadFiles();
    } catch (error) {
      message.error("Помилка видалення файлу");
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success("Скопійовано");
    } catch {
      message.error("Не вдалося скопіювати");
    }
  };

  if (!isEditMode || !appId) {
    return (
      <Alert
        type="info"
        showIcon
        message="Спочатку збережіть застосунок"
        description="Файли домену прив'язуються до застосунку. Створіть і збережіть його, після чого тут можна буде заливати файли."
      />
    );
  }

  const columns: ColumnsType<AppDomainFile> = [
    {
      title: "Шлях / URL",
      dataIndex: "path",
      key: "path",
      render: (p: string) => (
        <Space direction="vertical" size={0}>
          <Space size="small">
            <Text code>{p}</Text>
            <Tooltip title="Скопіювати шлях">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copy(p)}
              />
            </Tooltip>
          </Space>
          {domain && (
            <Space size="small">
              <Link href={publicUrl(p)} target="_blank" rel="noreferrer">
                {publicUrl(p)}
              </Link>
              <Tooltip title="Скопіювати повний URL">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copy(publicUrl(p))}
                />
              </Tooltip>
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: "Файл",
      dataIndex: "filename",
      key: "filename",
      render: (name: string, record) => (
        <Space direction="vertical" size={0}>
          <Text>{name}</Text>
          {record.mimeType && (
            <Tag color="default" style={{ marginInlineEnd: 0 }}>
              {record.mimeType}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Розмір",
      dataIndex: "size",
      key: "size",
      width: 110,
      render: (size?: number | null) => formatBytes(size),
    },
    {
      title: "Дії",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Скачати">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Tooltip title="Видалити">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const normalizedPreview = normalizePath(path);

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="Файли, залиті сюди, віддаються воркером апки за публічним HTTPS-шляхом"
        description={
          <>
            Вкажіть шлях віддачі (напр. <Text code>/assets/app.js</Text>) і
            залийте файл — після{" "}
            <Text strong>повторного деплою застосунку</Text> він буде доступний
            у рантаймі за адресою{" "}
            <Text code>
              https://{domain || "<домен>"}
              {"<шлях>"}
            </Text>
            . Заміна вмісту за наявним шляхом підхоплюється одразу; нові шляхи
            потребують редеплою воркера.
          </>
        }
      />

      <Space.Compact style={{ width: "100%", maxWidth: 720 }}>
        <Input
          addonBefore="Шлях"
          placeholder="/assets/app.js"
          value={path}
          onChange={(e) => setPath(e.target.value)}
        />
        <Upload
          customRequest={handleUpload}
          showUploadList={false}
          maxCount={1}
          disabled={!normalizedPreview || normalizedPreview === "/"}
        >
          <Button
            type="primary"
            icon={<UploadOutlined />}
            loading={uploading}
            disabled={!normalizedPreview || normalizedPreview === "/"}
          >
            Залити файл
          </Button>
        </Upload>
      </Space.Compact>
      {normalizedPreview && domain && (
        <Text type="secondary">
          Буде доступний за: <Text code>{publicUrl(normalizedPreview)}</Text>
        </Text>
      )}

      <div>
        <Space style={{ marginBottom: 8 }}>
          <Button
            icon={<ReloadOutlined />}
            size="small"
            onClick={loadFiles}
            loading={loading}
          >
            Оновити
          </Button>
        </Space>
        <Table
          rowKey="id"
          dataSource={files}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      </div>
    </Space>
  );
};
